import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  WritableSignal
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'evland-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: [ './pagination.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIcon,
    ReactiveFormsModule,
  ],
})
export class PaginationComponent implements OnChanges, OnDestroy {
  initValue = input<{ page?: number; pageSize?: number; }>({});
  siblingCount = input<number>(1);
  total = input<number>(0);
  pageSizes = input<number[]>([]);
  isLoading = input<boolean>(false);
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  DOTS = '…';

  pageSizeControl: UntypedFormControl;
  currentPage: WritableSignal<number> = signal(1);
  pagesRange: WritableSignal<(number | string)[]> = signal([]);

  get pageSize(): number {
    return this.pageSizeControl.value;
  }

  private readonly destroyed$ = new Subject<void>();

  #fb = inject(UntypedFormBuilder);

  constructor() {
    this.initPageSizeControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { initValue, total } = changes;

    if (initValue) {
      const { page, pageSize } = initValue.currentValue;

      this.currentPage.set(page);

      this.pageSizeControl.reset(pageSize || this.pageSizes()[0], { emitEvent: false });
    }

    if (total) this.refreshPagesRange();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onNextPageClick(): void {
    const currentPage = this.currentPage();
    const pagesRange = this.pagesRange();

    if (currentPage >= (pagesRange[pagesRange.length - 1] as number)) {
      return;
    }

    this.updateCurrentPage(currentPage + 1);
  }

  onPageClick(page: number): void {
    this.updateCurrentPage(page);
  }

  onPrevPageClick(): void {
    const currentPage = this.currentPage();

    if (currentPage <= 1) {
      return;
    }

    this.updateCurrentPage(currentPage - 1);
  }

  private initPageSizeControl(): void {
    this.pageSizeControl = this.#fb.control(null, Validators.required);

    this.pageSizeControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      ).subscribe((pageSize: number) => {
      this.pageSizeChange.emit(+pageSize);
      this.refreshPagesRange();
    });
  }

  private range(start: number, end: number): (number | string)[] {
    const length = end - start + 1;

    return Array.from({ length }, (_, idx) => idx + start);
  }

  private calculatePagesRange(): (number | string)[] {
    const currentPage = this.currentPage();
    const siblingCount = this.siblingCount();

    const totalPageCount = Math.ceil(this.total() / this.pageSize);
    const totalPageNumbers = siblingCount + 3;

    if (totalPageNumbers >= totalPageCount) {
      return this.range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;
    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = this.range(1, leftItemCount);

      return [ ...leftRange, this.DOTS, totalPageCount ];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = this.range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );

      return [ firstPageIndex, this.DOTS, ...rightRange ];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = this.range(leftSiblingIndex, rightSiblingIndex);

      return [ firstPageIndex, this.DOTS, ...middleRange, this.DOTS, lastPageIndex ];
    }

    return [ 1 ];
  }

  private refreshPagesRange(): void {
    this.pagesRange.set(this.calculatePagesRange());
  }

  private updateCurrentPage(page: number): void {
    this.currentPage.set(page);
    this.pageChange.emit(this.currentPage());
    this.refreshPagesRange();
  }
}
